import { PeopleSdk } from 'lib-klab-sdks';

type PagedProductivityRuleResponse = PeopleSdk.PagedProductivityRuleResponse;
type ProductivityRuleResponse = PeopleSdk.ProductivityRuleResponse;
type ProductivityRuleCreateRequest = PeopleSdk.ProductivityRuleCreateRequest;
type ProductivityRuleUpdateRequest = PeopleSdk.ProductivityRuleUpdateRequest;

function buildPage(
    number: number,
    size: number,
    totalElements: number
): PeopleSdk.PaginationResponse {
    return {
        number,
        size,
        totalElements,
        totalPages: Math.max(1, Math.ceil(totalElements / size)),
    };
}

const dept = (id: number, name: string): PeopleSdk.DepartmentObject => ({
    departmentId: id,
    name,
});

function buildRule(
    productivityRuleId: number,
    departmentId: number,
    departmentName: string,
    type: PeopleSdk.ActivityTypeEnum,
    identifier: string,
    classification: PeopleSdk.ClassificationEnum,
    isEnabled = true,
): ProductivityRuleResponse {
    return {
        productivityRuleId,
        department: dept(departmentId, departmentName),
        type,
        identifier,
        classification,
        isEnabled,
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
        employees: undefined,
    };
}

const WEBSITE_IDENTIFIERS = [
    'www.notion.com',
    'www.github.com',
    'www.linkedin.com',
    'www.youtube.com',
    'www.netflix.com',
];
const APP_IDENTIFIERS = ['Slack', 'Spotify', 'Figma', 'VSCode', 'Teams', 'Discord'];
const DEPARTMENTS = [
    { id: 1, name: 'Desarrollo' },
    { id: 2, name: 'QA' },
    { id: 3, name: 'RRHH' },
] as const;
const CLASSIFICATIONS: PeopleSdk.ClassificationEnum[] = [
    PeopleSdk.ClassificationEnumValues.Productive,
    PeopleSdk.ClassificationEnumValues.Neutral,
    PeopleSdk.ClassificationEnumValues.Unproductive,
];

function buildMockContent(type?: PeopleSdk.ActivityTypeEnum): ProductivityRuleResponse[] {
    let id = 1;
    const webList = WEBSITE_IDENTIFIERS.map((identifier, i) => {
        const dept = DEPARTMENTS[i % DEPARTMENTS.length];
        return buildRule(
            id++,
            dept.id,
            dept.name,
            PeopleSdk.ActivityTypeEnumValues.Web,
            identifier,
            CLASSIFICATIONS[i % CLASSIFICATIONS.length]
        );
    });
    const appList = APP_IDENTIFIERS.map((identifier, i) => {
        const dept = DEPARTMENTS[i % DEPARTMENTS.length];
        return buildRule(
            id++,
            dept.id,
            dept.name,
            PeopleSdk.ActivityTypeEnumValues.App,
            identifier,
            CLASSIFICATIONS[i % CLASSIFICATIONS.length]
        );
    });
    if (type === 'APP') return appList;
    if (type === 'WEB') return webList;
    return [...webList, ...appList];
}

let mockStore: ProductivityRuleResponse[] = buildMockContent();

function getAllProductivityRules(
    page = 0,
    size = 20,
    type?: PeopleSdk.ActivityTypeEnum,
    departmentId?: number
): PagedProductivityRuleResponse {
    let list = [...mockStore];
    if (type) {
        list = list.filter((r) => r.type === type);
    }
    if (departmentId != null) {
        list = list.filter((r) => (r.department?.departmentId ?? r.departments?.[0]?.departmentId) === departmentId);
    }
    const total = list.length;
    const p = Math.max(0, page);
    const s = Math.max(1, size);
    const start = p * s;
    const content = list.slice(start, start + s);
    return {
        content,
        page: buildPage(p, s, total),
    };
}

function getProductivityRuleById(productivityRuleId: number): ProductivityRuleResponse {
    const rule = mockStore.find((r) => r.productivityRuleId === productivityRuleId);
    if (!rule) throw { status: 404, message: 'Regla no encontrada' };
    return { ...rule };
}


function buildDepartmentsFromIds(departmentIds?: number[]): PeopleSdk.DepartmentObject[] {
    return (departmentIds ?? []).map((id) => {
        const found = DEPARTMENTS.find((d) => d.id === id);
        return dept(id, found?.name ?? `Departamento ${id}`);
    });
}

function buildDepartmentRules(
    payload: ProductivityRuleCreateRequest | ProductivityRuleUpdateRequest,
    departments: PeopleSdk.DepartmentObject[]
): PeopleSdk.ProductivityRuleDepartmentRule[] {
    if (payload.departmentRules?.length) {
        return payload.departmentRules.map((dr) => ({
            departmentId: dr.departmentId,
            name: departments.find((d) => d.departmentId === dr.departmentId)?.name
                ?? `Departamento ${dr.departmentId}`,
            classification: dr.classification,
        }));
    }
    return departments.map((d) => ({
        departmentId: d.departmentId,
        name: d.name,
        classification: payload.classification,
    }));
}

function deriveGroupClassification(
    departmentRules: PeopleSdk.ProductivityRuleDepartmentRule[],
    fallback: PeopleSdk.ClassificationEnum
): PeopleSdk.ClassificationEnum {
    const distinct = new Set(departmentRules.map((dr) => dr.classification));
    if (distinct.size > 1) return PeopleSdk.ClassificationEnumValues.Custom;
    return departmentRules[0]?.classification ?? fallback;
}

function createProductivityRule(
    payload: ProductivityRuleCreateRequest
): ProductivityRuleResponse {
    const nextId = Math.max(0, ...mockStore.map((r) => r.productivityRuleId)) + 1;
    const departments = buildDepartmentsFromIds(payload.departmentIds);
    const primary = departments[0] ?? dept(DEPARTMENTS[0].id, DEPARTMENTS[0].name);
    const departmentRules = buildDepartmentRules(payload, departments);
    const rule: ProductivityRuleResponse = {
        productivityRuleId: nextId,
        department: primary,
        departments,
        departmentRules,
        type: payload.type,
        identifier: payload.identifier,
        classification: deriveGroupClassification(departmentRules, payload.classification),
        isEnabled: true,
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: new Date().toISOString(),
        employees: undefined,
    };
    mockStore = [...mockStore, rule];
    return rule;
}

function updateProductivityRule(
    productivityRuleId: number,
    payload: ProductivityRuleUpdateRequest
): void {
    const idx = mockStore.findIndex((r) => r.productivityRuleId === productivityRuleId);
    if (idx === -1) throw { status: 404, message: 'Regla no encontrada' };
    const departments = buildDepartmentsFromIds(payload.departmentIds);
    const primary = departments[0] ?? dept(DEPARTMENTS[0].id, DEPARTMENTS[0].name);
    const departmentRules = buildDepartmentRules(payload, departments);
    mockStore = mockStore.map((r, i) =>
        i === idx
            ? {
                  ...r,
                  department: primary,
                  departments,
                  departmentRules,
                  type: payload.type,
                  identifier: payload.identifier,
                  classification: deriveGroupClassification(departmentRules, payload.classification),
                  employees: undefined,
                  updatedAt: new Date().toISOString(),
              }
            : r
    );
}

function deleteProductivityRuleById(productivityRuleId: number): void {
    const prev = mockStore.length;
    mockStore = mockStore.filter((r) => r.productivityRuleId !== productivityRuleId);
    if (mockStore.length === prev) throw { status: 404, message: 'Regla no encontrada' };
}

export const productivityRulesSdkMock = {
    getAllProductivityRules,
    getProductivityRuleById,
    createProductivityRule,
    updateProductivityRule,
    deleteProductivityRuleById,
};
